import React, { useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ConfirmDeleteDialog, SwipeRow } from "@/components/SwipeDelete";

// Selectable list with bulk delete, swipe-to-delete and a per-row bin icon.
export default function BulkDeleteList({ items, header, renderRow, kind, noun, lockedIds = [], deletePath, bulkPath, onDone, empty }) {
  const [selected, setSelected] = useState([]);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const selectable = items.filter((i) => !lockedIds.includes(i.id)).map((i) => i.id);
  const allSelected = selectable.length > 0 && selectable.every((id) => selected.includes(id));
  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const plural = (n) => `${n} ${noun}${n === 1 ? "" : "s"}`;

  async function confirm() {
    setBusy(true);
    try {
      const { data } = pending.ids.length === 1 ? await api.delete(deletePath(pending.ids[0])) : await api.post(bulkPath, { ids: pending.ids });
      const n = pending.ids.length;
      toast.success(`Deleted ${n === 1 ? pending.label : plural(n)}`, {
        description: "Moved to Recently deleted for 30 days.",
        duration: 10000,
        action: { label: "Undo", onClick: () => undo(data.batch_id) },
      });
      setSelected((s) => s.filter((id) => !pending.ids.includes(id)));
      setPending(null);
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function undo(batchId) {
    try {
      const { data } = await api.post("/admin/trash/restore", { batch_id: batchId });
      toast.success(`Restored ${data.restored === 1 ? data.labels[0] : plural(data.restored)}`);
      data.errors?.forEach((e) => toast.error(e));
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  const ask = (ids, label) => setPending({ ids, label });

  return (
    <>
      {selected.length > 0 && (
        <div className="sticky top-20 z-20 mt-4 flex items-center gap-3 flex-wrap rounded-2xl bg-neutral-900 text-white px-4 py-3 shadow-lg animate-fade-up" data-testid={`${kind}-bulk-bar`}>
          <span className="text-sm font-semibold" data-testid={`${kind}-selected-count`}>{selected.length} selected</span>
          <button data-testid={`${kind}-bulk-delete`} onClick={() => ask(selected, plural(selected.length))} className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-xs font-semibold bg-red-500 hover:bg-red-400 transition-colors"><Trash2 size={13} /> Delete selected</button>
          <button data-testid={`${kind}-bulk-clear`} onClick={() => setSelected([])} className="ml-auto text-xs text-white/70 hover:text-white">Clear</button>
        </div>
      )}
      <div className="soft-card mt-4" style={{ overflowX: "clip", overflowY: "visible" }} data-testid={`${kind}-list`}>
        <div className="flex items-center gap-3 px-4 sm:px-5 py-3 bg-neutral-50 rounded-t-[inherit] text-xs text-neutral-500 uppercase tracking-wider font-semibold">
          <input type="checkbox" data-testid={`${kind}-select-all`} disabled={!selectable.length} checked={allSelected} onChange={() => setSelected(allSelected ? [] : selectable)} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select all" />
          <div className="flex-1 min-w-0">{header}</div>
          <span className="w-8" />
        </div>
        {items.map((it) => {
          const locked = lockedIds.includes(it.id);
          return (
            <SwipeRow key={it.id} disabled={locked} testId={`${kind}-row-${it.id}`} onDelete={() => ask([it.id], it.__label || noun)}>
              <div className={`flex items-center gap-3 px-4 sm:px-5 py-3 border-t border-neutral-100 transition-colors ${selected.includes(it.id) ? "bg-propera-light/40" : ""}`}>
                {locked ? <span className="w-4 h-4 flex-shrink-0" /> : <input type="checkbox" data-testid={`${kind}-select-${it.id}`} checked={selected.includes(it.id)} onChange={() => toggle(it.id)} className="w-4 h-4 accent-[#0E8C5A] cursor-pointer" aria-label="Select" />}
                <div className="flex-1 min-w-0">{renderRow(it)}</div>
                {locked ? <span className="w-8" /> : (
                  <button data-testid={`${kind}-delete-${it.id}`} onClick={() => ask([it.id], it.__label || noun)} className="p-2 rounded-full text-neutral-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Delete"><Trash2 size={15} /></button>
                )}
              </div>
            </SwipeRow>
          );
        })}
        {items.length === 0 && <p className="p-6 text-sm text-neutral-500 text-center">{empty}</p>}
      </div>
      <ConfirmDeleteDialog
        open={!!pending}
        busy={busy}
        title={pending ? `Delete ${pending.ids.length === 1 ? pending.label : plural(pending.ids.length)}?` : ""}
        description={`This removes ${pending ? plural(pending.ids.length) : ""} and everything linked to ${pending?.ids.length === 1 ? "it" : "them"}. You can undo straight away, or restore from Recently deleted within 30 days.`}
        confirmLabel={pending ? `Delete ${plural(pending.ids.length)}` : "Delete"}
        onCancel={() => setPending(null)}
        onConfirm={confirm}
      />
    </>
  );
}
